import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import {
  CheckCircle, AlertTriangle, XCircle, Clock, Activity,
  Search, ArrowUpRight, Bell, ChevronDown, ChevronUp,
  ExternalLink, Mail, Shield, Zap, Globe, RefreshCw,
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip as RechartsTooltip,
  BarChart, Bar, Cell,
} from "recharts";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  operational: { color: "#10b981", bg: "rgba(16,185,129,0.1)", label: "Operational", icon: CheckCircle },
  degraded: { color: "#f59e0b", bg: "rgba(245,158,11,0.1)", label: "Degraded", icon: AlertTriangle },
  partial_outage: { color: "#f97316", bg: "rgba(249,115,22,0.1)", label: "Partial Outage", icon: AlertTriangle },
  major_outage: { color: "#ef4444", bg: "rgba(239,68,68,0.1)", label: "Major Outage", icon: XCircle },
};

const SEVERITY_COLORS = {
  warning: "#f59e0b",
  minor: "#f97316",
  major: "#ef4444",
};

// Uptime calendar cell colors
function uptimeColor(pct) {
  if (pct >= 99.5) return "#10b981";
  if (pct >= 99) return "#34d399";
  if (pct >= 97) return "#fbbf24";
  if (pct >= 95) return "#f97316";
  return "#ef4444";
}

function timeAgo(isoStr) {
  if (!isoStr) return "N/A";
  const diff = Date.now() - new Date(isoStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// Mini sparkline component
function Sparkline({ data, width = 120, height = 32 }) {
  if (!data || data.length === 0) return <div style={{ width, height }} />;
  return (
    <ResponsiveContainer width={width} height={height}>
      <AreaChart data={data} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
        <defs>
          <linearGradient id="sparkGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#10b981" stopOpacity={0.3} />
            <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
          </linearGradient>
        </defs>
        <Area type="monotone" dataKey="latency_p50_ms" stroke="#10b981" fill="url(#sparkGrad)" strokeWidth={1.5} dot={false} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

// Uptime calendar (90 days)
function UptimeCalendar({ days, providerName }) {
  if (!days || days.length === 0) return null;
  const sorted = [...days].sort((a, b) => a.date.localeCompare(b.date)).slice(-90);
  const avgUptime = sorted.length > 0
    ? (sorted.reduce((s, d) => s + d.uptime_pct, 0) / sorted.length).toFixed(2)
    : "0";

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-[var(--qn-foreground-muted)]">90-day uptime</span>
        <span className="text-xs font-mono font-semibold" style={{ color: uptimeColor(parseFloat(avgUptime)) }}>
          {avgUptime}%
        </span>
      </div>
      <TooltipProvider>
        <div className="flex gap-[2px] flex-wrap">
          {sorted.map((d, i) => (
            <Tooltip key={i}>
              <TooltipTrigger asChild>
                <div
                  className="rounded-[2px] cursor-pointer hover:ring-1 hover:ring-white/40 transition-all"
                  style={{
                    width: 8, height: 8,
                    backgroundColor: uptimeColor(d.uptime_pct),
                    opacity: 0.9,
                  }}
                />
              </TooltipTrigger>
              <TooltipContent side="top" className="text-xs">
                <p className="font-semibold">{d.date}</p>
                <p>Uptime: {d.uptime_pct}%</p>
                {d.incidents_count > 0 && <p className="text-orange-400">{d.incidents_count} incident(s)</p>}
              </TooltipContent>
            </Tooltip>
          ))}
        </div>
      </TooltipProvider>
    </div>
  );
}

// Overall status banner
function StatusBanner({ summary }) {
  if (!summary) return null;
  const cfg = STATUS_CONFIG[summary.overall_status] || STATUS_CONFIG.operational;
  const Icon = cfg.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl border px-6 py-4 mb-8"
      style={{
        borderColor: cfg.color + "40",
        background: cfg.bg,
      }}
      data-testid="status-banner"
    >
      <div className="flex items-center gap-3">
        <Icon className="w-6 h-6" style={{ color: cfg.color }} />
        <div>
          <h2 className="text-lg font-semibold" style={{ color: cfg.color }}>{cfg.label}</h2>
          <p className="text-sm text-[var(--qn-foreground-muted)]">
            {summary.operational}/{summary.total_providers} providers operational
            {summary.active_incidents > 0 && ` — ${summary.active_incidents} active incident(s)`}
          </p>
        </div>
        <div className="ml-auto text-right">
          <div className="text-xs text-[var(--qn-foreground-muted)]">Last updated</div>
          <div className="text-sm font-mono">{timeAgo(summary.last_updated)}</div>
        </div>
      </div>
    </motion.div>
  );
}

// Provider row in the status list
function ProviderRow({ provider, sparklineData, uptimeData, index }) {
  const [expanded, setExpanded] = useState(false);
  const status = provider.status || "operational";
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.operational;
  const Icon = cfg.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.015, 0.8) }}
      data-testid={`provider-row-${provider.provider_slug}`}
    >
      <div
        className="flex items-center gap-4 px-4 py-3 rounded-lg cursor-pointer hover:bg-[var(--qn-surface-hover)] transition-colors border border-transparent hover:border-[var(--qn-border)]"
        onClick={() => setExpanded(!expanded)}
      >
        <Icon className="w-4 h-4 shrink-0" style={{ color: cfg.color }} />
        <span className="font-medium text-sm flex-1 min-w-0 truncate">{provider.provider_name}</span>
        <Sparkline data={sparklineData} width={100} height={24} />
        <span className="text-xs font-mono w-16 text-right" style={{ color: provider.latency_ms > 2000 ? "#f59e0b" : "var(--qn-foreground-muted)" }}>
          {Math.round(provider.latency_ms)}ms
        </span>
        <Badge
          className="text-[10px] w-24 justify-center"
          style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}30` }}
        >
          {cfg.label}
        </Badge>
        {expanded ? <ChevronUp className="w-4 h-4 text-[var(--qn-foreground-muted)]" /> : <ChevronDown className="w-4 h-4 text-[var(--qn-foreground-muted)]" />}
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 pt-1 ml-8">
              <UptimeCalendar days={uptimeData} providerName={provider.provider_name} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// Latency chart
function LatencyChart({ data }) {
  if (!data || data.length === 0) return null;
  const top20 = data.slice(0, 20);

  return (
    <ResponsiveContainer width="100%" height={280}>
      <BarChart data={top20} margin={{ top: 10, right: 10, bottom: 60, left: 10 }}>
        <XAxis
          dataKey="provider_name"
          tick={{ fontSize: 10, fill: "var(--qn-foreground-muted)" }}
          angle={-45}
          textAnchor="end"
          interval={0}
        />
        <YAxis
          tick={{ fontSize: 10, fill: "var(--qn-foreground-muted)" }}
          label={{ value: "ms", position: "insideTopLeft", fontSize: 10 }}
        />
        <RechartsTooltip
          contentStyle={{
            background: "var(--qn-card)",
            border: "1px solid var(--qn-border)",
            borderRadius: 8,
            fontSize: 12,
            color: "var(--qn-foreground)",
          }}
          wrapperStyle={{ outline: "none" }}
          cursor={{ fill: "rgba(255,255,255,0.06)" }}
        />
        <Bar dataKey="avg_latency_ms" radius={[4, 4, 0, 0]}>
          {top20.map((entry, i) => (
            <Cell
              key={i}
              fill={entry.avg_latency_ms < 100 ? "#10b981" : entry.avg_latency_ms < 200 ? "#3b82f6" : entry.avg_latency_ms < 500 ? "#f59e0b" : "#ef4444"}
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

// Incident timeline
function IncidentTimeline({ incidents }) {
  if (!incidents || incidents.length === 0) {
    return (
      <div className="text-center py-8 text-[var(--qn-foreground-muted)] text-sm">
        No recent incidents — all systems nominal
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {incidents.slice(0, 10).map((inc, i) => {
        const sevColor = SEVERITY_COLORS[inc.severity] || "#f59e0b";
        const isActive = !inc.resolved_at;
        return (
          <motion.div
            key={inc.incident_id}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.05 }}
            className="flex gap-3 items-start p-3 rounded-lg border border-[var(--qn-border)] hover:bg-[var(--qn-surface-hover)] transition-colors"
            data-testid={`incident-${inc.incident_id}`}
          >
            <div className="mt-1 w-2 h-2 rounded-full shrink-0" style={{ background: isActive ? sevColor : "#6b7280" }} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium">{inc.provider_name}</span>
                <Badge
                  className="text-[10px]"
                  style={{ background: sevColor + "20", color: sevColor, border: `1px solid ${sevColor}30` }}
                >
                  {inc.severity}
                </Badge>
                {isActive && (
                  <Badge className="text-[10px] bg-red-500/20 text-red-400 border border-red-500/30">ACTIVE</Badge>
                )}
              </div>
              <div className="text-xs text-[var(--qn-foreground-muted)] mt-1">
                {new Date(inc.started_at).toLocaleString()}
                {inc.resolved_at && ` — Resolved ${timeAgo(inc.resolved_at)}`}
              </div>
              {inc.timeline && inc.timeline.length > 0 && (
                <p className="text-xs text-[var(--qn-foreground-muted)] mt-1 italic">
                  {inc.timeline[0].message}
                </p>
              )}
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}

export default function StatusPage() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [providers, setProviders] = useState([]);
  const [latencyData, setLatencyData] = useState([]);
  const [uptimeData, setUptimeData] = useState({});
  const [sparklines, setSparklines] = useState({});
  const [incidents, setIncidents] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [subEmail, setSubEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [statusRes, latencyRes, uptimeRes, incidentRes] = await Promise.all([
        axios.get(`${API}/status/providers`),
        axios.get(`${API}/status/latency`),
        axios.get(`${API}/status/uptime?days=90`),
        axios.get(`${API}/status/incidents?limit=20`),
      ]);

      setProviders(statusRes.data.providers || []);
      setSummary(statusRes.data.summary);
      setLatencyData(latencyRes.data.providers || []);
      setIncidents(incidentRes.data.incidents || []);

      // Build uptime lookup
      const uptMap = {};
      (uptimeRes.data.providers || []).forEach(p => {
        uptMap[p.provider_slug] = p.days || [];
      });
      setUptimeData(uptMap);

      // Fetch sparklines for visible providers
      const slugs = (statusRes.data.providers || []).map(p => p.provider_slug);
      const sparkPromises = slugs.slice(0, 30).map(slug =>
        axios.get(`${API}/status/sparkline/${slug}?hours=24`).then(r => ({ slug, data: r.data.points })).catch(() => ({ slug, data: [] }))
      );
      const sparkResults = await Promise.all(sparkPromises);
      const sparkMap = {};
      sparkResults.forEach(r => { sparkMap[r.slug] = r.data; });
      setSparklines(sparkMap);
    } catch (err) {
      console.error("Failed to fetch status data:", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!subEmail) return;
    setSubscribing(true);
    try {
      await axios.post(`${API}/status/subscribe`, { email: subEmail });
      toast.success("Subscribed to status updates!");
      setSubEmail("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Subscription failed");
    } finally {
      setSubscribing(false);
    }
  };

  const filteredProviders = providers.filter(p =>
    p.provider_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.provider_slug?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--qn-background)] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-[var(--qn-primary)] border-t-transparent rounded-full animate-spin" />
          <div className="text-[var(--qn-foreground-muted)]">Loading status data...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--qn-background)] text-[var(--qn-foreground)]">
      {/* Header */}
      <header className="border-b border-[var(--qn-border)] bg-[var(--qn-card)]">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/")} data-testid="status-logo">
            <Shield className="w-6 h-6 text-[var(--qn-primary)]" />
            <span className="font-bold text-xl tracking-tight">QNNEAL</span>
            <span className="text-[var(--qn-foreground-muted)] text-sm ml-1">Status</span>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
              data-testid="refresh-btn"
              className="text-[var(--qn-foreground-muted)]"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/")}
              data-testid="back-to-qnneal"
            >
              <Globe className="w-4 h-4 mr-1" />
              QNNEAL.com
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Status Banner */}
        <StatusBanner summary={summary} />

        {/* Stats Row */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[
              { label: "Providers Monitored", value: summary.total_providers, icon: Activity, color: "#3b82f6" },
              { label: "Operational", value: summary.operational, icon: CheckCircle, color: "#10b981" },
              { label: "Platform Uptime", value: `${summary.uptime_pct}%`, icon: Shield, color: "#8b5cf6" },
              { label: "Active Incidents", value: summary.active_incidents, icon: AlertTriangle, color: summary.active_incidents > 0 ? "#f59e0b" : "#10b981" },
            ].map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
              >
                <Card className="border-[var(--qn-border)] bg-[var(--qn-card)]" data-testid={`stat-${stat.label.toLowerCase().replace(/ /g, "-")}`}>
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: stat.color + "15" }}>
                      <stat.icon className="w-5 h-5" style={{ color: stat.color }} />
                    </div>
                    <div>
                      <div className="text-2xl font-bold font-mono">{stat.value}</div>
                      <div className="text-xs text-[var(--qn-foreground-muted)]">{stat.label}</div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}

        {/* Provider Status List */}
        <Card className="border-[var(--qn-border)] bg-[var(--qn-card)] mb-8" data-testid="provider-status-list">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <CardTitle className="text-lg">Provider Status</CardTitle>
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--qn-foreground-muted)]" />
                <Input
                  placeholder="Search providers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 h-9 bg-[var(--qn-background)] border-[var(--qn-border)]"
                  data-testid="provider-search"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="divide-y divide-[var(--qn-border)]/50">
              {filteredProviders.map((provider, i) => (
                <ProviderRow
                  key={provider.provider_slug}
                  provider={provider}
                  sparklineData={sparklines[provider.provider_slug]}
                  uptimeData={uptimeData[provider.provider_slug]}
                  index={i}
                />
              ))}
              {filteredProviders.length === 0 && (
                <div className="text-center py-8 text-[var(--qn-foreground-muted)] text-sm">
                  No providers match your search
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Two-column: Latency Chart + Incidents */}
        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          <Card className="border-[var(--qn-border)] bg-[var(--qn-card)]" data-testid="latency-chart">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Zap className="w-4 h-4 text-[var(--qn-primary)]" />
                Latency Comparison
              </CardTitle>
              <p className="text-xs text-[var(--qn-foreground-muted)]">Average response time (top 20 fastest)</p>
            </CardHeader>
            <CardContent>
              <LatencyChart data={latencyData} />
            </CardContent>
          </Card>

          <Card className="border-[var(--qn-border)] bg-[var(--qn-card)]" data-testid="incident-timeline">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="w-4 h-4 text-[var(--qn-primary)]" />
                Recent Incidents
              </CardTitle>
              <p className="text-xs text-[var(--qn-foreground-muted)]">Last 10 incidents across all providers</p>
            </CardHeader>
            <CardContent>
              <IncidentTimeline incidents={incidents} />
            </CardContent>
          </Card>
        </div>

        {/* D-Wave Quantum Engine Status */}
        <div className="rounded-xl border border-[var(--qn-border)] bg-[var(--qn-surface,var(--qn-card))] p-4 mb-8" data-testid="status-dwave-widget">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"/>
              <span className="font-medium text-[var(--qn-foreground)]">D-Wave Quantum Engine</span>
            </div>
            <span className="text-xs text-emerald-400">QPU Available</span>
          </div>
          <div className="grid grid-cols-4 gap-2 text-xs text-center">
            <div><div className="font-bold text-cyan-400">1,000/s</div><div className="text-[var(--qn-foreground-subtle,var(--qn-foreground-muted))]">Annealing</div></div>
            <div><div className="font-bold text-emerald-400">Active</div><div className="text-[var(--qn-foreground-subtle,var(--qn-foreground-muted))]">Classical</div></div>
            <div><div className="font-bold text-purple-400">5,760</div><div className="text-[var(--qn-foreground-subtle,var(--qn-foreground-muted))]">Qubits</div></div>
            <div><div className="font-bold text-amber-400">99.7%</div><div className="text-[var(--qn-foreground-subtle,var(--qn-foreground-muted))]">Uptime</div></div>
          </div>
        </div>

        {/* Subscribe Section */}
        <Card className="border-[var(--qn-border)] bg-[var(--qn-card)]" data-testid="subscribe-section">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-center gap-4">
              <div className="flex items-center gap-3 flex-1">
                <div className="w-10 h-10 rounded-lg bg-[var(--qn-primary)]/10 flex items-center justify-center">
                  <Bell className="w-5 h-5 text-[var(--qn-primary)]" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm">Subscribe to Status Updates</h3>
                  <p className="text-xs text-[var(--qn-foreground-muted)]">
                    Get notified when providers experience issues or recover
                  </p>
                </div>
              </div>
              <form onSubmit={handleSubscribe} className="flex gap-2 w-full md:w-auto">
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={subEmail}
                  onChange={(e) => setSubEmail(e.target.value)}
                  className="h-9 w-64 bg-[var(--qn-background)] border-[var(--qn-border)]"
                  data-testid="subscribe-email"
                  required
                />
                <Button
                  type="submit"
                  size="sm"
                  disabled={subscribing}
                  data-testid="subscribe-btn"
                  className="bg-[var(--qn-primary)] hover:bg-[var(--qn-primary)]/90"
                >
                  {subscribing ? "..." : "Subscribe"}
                </Button>
              </form>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center py-8 text-xs text-[var(--qn-foreground-muted)]">
          <p>Powered by QNNEAL Health Monitoring Engine</p>
          <p className="mt-1">Checks run every 5 minutes across all {summary?.total_providers || 69} providers</p>
        </div>
      </main>
    </div>
  );
}
